import React from 'react';
import { cn } from './cn';

export type StatTone = 'neutral' | 'forest' | 'trigo' | 'warning' | 'danger' | 'info';
export type DeltaTone = 'up' | 'down' | 'flat';

const toneChip: Record<StatTone, string> = {
  neutral: 'bg-paper-2 text-sage',
  forest: 'bg-forest/10 text-forest',
  trigo: 'bg-trigo/15 text-ink',
  warning: 'bg-warning/10 text-warning',
  danger: 'bg-danger/10 text-danger',
  info: 'bg-info/10 text-info',
};

const deltaTone: Record<DeltaTone, string> = {
  up: 'text-success',
  down: 'text-danger',
  flat: 'text-sage',
};

const deltaArrow: Record<DeltaTone, string> = {
  up: '↑',
  down: '↓',
  flat: '→',
};

export interface StatTileProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  /** Tooltip nativo (hover) sobre toda la tile. */
  title?: string;
  icon?: React.ReactNode;
  tone?: StatTone;
  delta?: { value: string; tone?: DeltaTone };
  onClick?: () => void;
  className?: string;
}

/**
 * Reemplazo limpio de StatCard: valor grande en Onest, etiqueta en salvia,
 * tinte del ícono por tono, delta opcional. Sin gradientes ni emojis.
 * Si recibe onClick, se vuelve clickeable con feedback sutil.
 */
export const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  hint,
  title,
  icon,
  tone = 'neutral',
  delta,
  onClick,
  className,
}) => {
  const interactive = typeof onClick === 'function';
  return (
    <div
      title={title}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-hairline bg-white p-4 shadow-card',
        interactive && 'cursor-pointer transition-colors hover:border-forest/30 hover:bg-paper-2/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-mid/60',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-sage">{label}</span>
        {icon && <span className={cn('flex h-8 w-8 items-center justify-center rounded-md', toneChip[tone])}>{icon}</span>}
      </div>
      <div className="font-display text-2xl font-extrabold leading-none tabular-nums text-ink md:text-3xl">{value}</div>
      {(hint || delta) && (
        <div className="flex items-center gap-2 text-xs text-sage">
          {delta && (
            <span className={cn('font-medium', deltaTone[delta.tone ?? 'flat'])}>
              {deltaArrow[delta.tone ?? 'flat']} {delta.value}
            </span>
          )}
          {hint && <span className="min-w-0 truncate">{hint}</span>}
        </div>
      )}
    </div>
  );
};

export default StatTile;
